/**
 * @file src/permissions/personas.js
 * @description
 *
 * 🎭 The four experiences Moksy actually has, above the forty-five roles it
 * actually stores.
 *
 * ── Why this exists ──────────────────────────────────────────────────
 *
 * The platform reasons in ROLES — `user`, `reviewer`, `support`,
 * `chief_security_officer` and forty-one more. People reason in PERSONAS:
 * am I browsing as a visitor, working on my own spec, reviewing somebody
 * else's, or operating the platform? Those are four answers, and every
 * surface that tried to derive them from raw roles grew its own list, which
 * then went stale the moment a role was added. The admin dashboard's role
 * chart is the cautionary tale: it hardcoded nine of forty-five and silently
 * showed a fifth of the platform.
 *
 * One mapping, in packages/common beside `reviewerSeparation`, because the
 * services and the dashboard have to agree about which hat somebody is
 * wearing or the navigation and the API will disagree about what they may do.
 *
 * ── The four ─────────────────────────────────────────────────────────
 *
 *   GUEST     Not a role and not an account. There is no User row: the
 *             gateway stamps a transient marker on the request. It is the
 *             ABSENCE of identity, which is why it can be previewed but never
 *             "held" — see PREVIEWABLE_PERSONAS.
 *   CUSTOMER  Has ideas, owns projects, buys reviews. The default.
 *   REVIEWER  Assesses other people's work. Accredited, and deliberately
 *             CONSTRAINED — see reviewerSeparation.js.
 *   STAFF     Operates the platform. Owner-group roles.
 *
 * ── One identity, several hats ───────────────────────────────────────
 *
 * A single account can hold customer AND reviewer — that is the normal shape
 * of a customer who was later accredited. Holding both does NOT collapse the
 * rules of either: the reviewer hat still cannot create projects, because the
 * integrity argument in reviewerSeparation.js is about what the PERSON can
 * do, not about which menu they are looking at. A switcher that quietly
 * granted customer powers to a reviewer would defeat a control the services
 * enforce anyway, and produce a UI that offers actions the API refuses.
 */

import { rolesOf, isOperator, isReviewer, mayCreateProjects } from './reviewerSeparation.js';
import { OWNER_ROLE_SET } from './ownerRoles.js';

export const PERSONAS = Object.freeze({
  GUEST: 'guest',
  CUSTOMER: 'customer',
  REVIEWER: 'reviewer',
  STAFF: 'staff'
});

export const PERSONA_LABELS = Object.freeze({
  [PERSONAS.GUEST]: 'Guest',
  [PERSONAS.CUSTOMER]: 'Customer',
  [PERSONAS.REVIEWER]: 'Reviewer',
  [PERSONAS.STAFF]: 'Staff'
});


// The staff persona IS the owner group. Imported rather than retyped: this
// file used to carry a ninth hand-maintained copy of the list, added by the
// persona work itself while the audit finding about eight copies was open.
const STAFF_ROLES = OWNER_ROLE_SET;

/** Which persona a single role belongs to. */
export const personaForRole = (role) => {
  const r = String(role || '').trim().toLowerCase();
  if (!r) return PERSONAS.GUEST;
  if (STAFF_ROLES.has(r)) return PERSONAS.STAFF;
  if (r === 'reviewer' || r === 'lead_reviewer') return PERSONAS.REVIEWER;
  return PERSONAS.CUSTOMER;
};

/**
 * Every persona this identity may wear, in a stable order.
 *
 * Derived from role + assignedRoles together: a promoted customer has
 * `role: 'user'` with `reviewer` in assignedRoles, and reading only `role`
 * would hide the very hat they were promoted into.
 *
 * GUEST is never included. It is not something an account can hold — an
 * account holder is by definition not a guest — so offering it here would
 * make the switcher lie.
 */
export const personasOf = (actor) => {
  if (!actor) return [];
  const roles = rolesOf(actor);
  const held = new Set();

  // 🔐 STAFF is granted, never assumed. Operating the platform carries real
  // weight, so it appears only for somebody who has actually been given an
  // owner-group or staff role — no amount of reviewing or buying earns it.
  if (isOperator(actor) || roles.some((r) => STAFF_ROLES.has(r))) held.add(PERSONAS.STAFF);
  if (isReviewer(actor)) held.add(PERSONAS.REVIEWER);

  // 👥 EVERY account holds the customer hat, reviewers included.
  //
  // This used to exclude reviewers outright, so an accredited reviewer
  // could not switch back to see their own projects, invoices or
  // downloads — work `reviewerSeparation` explicitly says they keep:
  // "reading, downloading or finishing projects a person already owned".
  // Withholding the whole persona to enforce a rule about CREATION took
  // away things the rule never touched, and left somebody who used the
  // product before being accredited locked out of their own history.
  //
  // ⚠️ Holding the hat is not holding the powers. `personaCapabilities`
  // still delegates `canCreateProjects` to reviewerSeparation, and the
  // services still refuse — so a reviewer wearing this hat sees their
  // existing work and is told, up front, why they cannot start new work.
  // That is the integrity rule surviving intact while stopping being a
  // navigation dead end.
  held.add(PERSONAS.CUSTOMER);

  const order = [PERSONAS.CUSTOMER, PERSONAS.REVIEWER, PERSONAS.STAFF];
  return order.filter((p) => held.has(p));
};

/**
 * The persona an actor should land in when they have not chosen one.
 *
 * Staff first: somebody with operator powers signing in almost always came to
 * operate. Then reviewer, because an accredited reviewer's day is review work.
 * Customer last as the universal fallback.
 */
export const defaultPersonaOf = (actor) => {
  const held = personasOf(actor);
  // No account is not "a customer with nothing yet" — it is the guest
  // experience, and saying otherwise would render signed-in navigation to
  // somebody who cannot use any of it.
  if (!held.length) return PERSONAS.GUEST;
  return held.find((p) => p === PERSONAS.STAFF)
    || held.find((p) => p === PERSONAS.REVIEWER)
    || PERSONAS.CUSTOMER;
};

/**
 * What a persona may do, as facts rather than as menu entries.
 *
 * `canCreateProjects` delegates to reviewerSeparation rather than restating
 * it — one rule, one place. The switcher uses this to explain the constraint
 * up front instead of letting somebody find it by being refused.
 */
export const personaCapabilities = (actor, persona) => {
  const p = String(persona || '').toLowerCase();
  if (p === PERSONAS.REVIEWER) {
    // Asked as a reviewer, regardless of what else the account holds: the hat
    // is the question, and an operator wearing the reviewer hat is reviewing.
    const verdict = mayCreateProjects({ role: 'reviewer' });
    return {
      canCreateProjects: verdict.allowed,
      restriction: verdict.allowed ? null : verdict.message,
      restrictionCode: verdict.allowed ? null : verdict.code
    };
  }
  const verdict = mayCreateProjects(actor);
  return {
    canCreateProjects: verdict.allowed,
    restriction: verdict.allowed ? null : verdict.message,
    restrictionCode: verdict.allowed ? null : verdict.code
  };
};

/**
 * Every persona a switcher should OFFER, including the ones this actor may
 * not wear, each with the reason.
 *
 * `personasOf` answers "what may they do"; this answers "what should they
 * see". They differ for exactly one case, and it is the case that matters: a
 * reviewer has no customer hat. Omitting it silently leaves somebody hunting
 * a control that was never rendered and concluding the feature is broken —
 * whereas a visible, disabled "Customer" row carrying the separation rule's
 * own wording turns a missing feature into an explained policy.
 *
 * @returns {Array<{ persona: string, label: string, available: boolean,
 *                   reason: string|null, role: string|null }>}
 */
export const personaOptionsOf = (actor) => {
  const held = new Set(personasOf(actor));
  const order = [PERSONAS.CUSTOMER, PERSONAS.REVIEWER, PERSONAS.STAFF];

  return order.map((persona) => {
    if (held.has(persona)) {
      // A hat can be available AND constrained. The customer hat for a
      // reviewer is exactly that: theirs to wear for existing work, but
      // it does not carry project creation. Surfacing it here means the
      // switcher can say so at the point of choosing, instead of the
      // person discovering it as an absent button.
      const capabilities = personaCapabilities(actor, persona);
      const constraint = (persona === PERSONAS.CUSTOMER && capabilities && !capabilities.canCreateProjects)
        ? 'Your existing projects, invoices and downloads. New projects are not available while you review.'
        : null;

      return {
        persona,
        label: PERSONA_LABELS[persona],
        available: true,
        reason: null,
        constraint,
        role: roleForPersona(actor, persona)
      };
    }
    // Every remaining absence is simply not granted — advertising "Staff"
    // to a customer would be noise, and worse, an invitation to ask for
    // something the platform deliberately does not hand out.
    //
    // The reviewer-without-customer case that used to be explained here is
    // gone: reviewers now hold the customer hat. What they still cannot do
    // is CREATE, and that is surfaced by `personaCapabilities` on the hat
    // itself — a constraint stated where the action is, rather than a
    // whole persona withheld.
    return null;
  }).filter(Boolean);
};

/**
 * Personas a STAFF operator may preview without becoming anybody.
 *
 * Guest is here and nowhere else: it is the one experience that cannot be
 * held, only simulated. A preview is a rendering choice, not an
 * authentication change — nothing about the session moves — which is what
 * separates it from impersonation, where a real approval is required because
 * a real other person's data is on screen.
 */
/**
 * The role string that best represents a persona FOR THIS ACTOR.
 *
 * The navigation catalog, the route guards and the sidebar all filter on a
 * single role, not on a persona — so wearing a hat has to resolve back to one
 * role or nothing renders. This is that resolution, and it is per-actor
 * because "the staff role" means `super_admin` for one operator and `support`
 * for another.
 *
 * Returns null when the actor cannot wear the persona at all, which callers
 * should treat as "refuse the switch" rather than "pick something".
 */
export const roleForPersona = (actor, persona) => {
  const p = String(persona || '').toLowerCase();
  const roles = rolesOf(actor);
  if (!personasOf(actor).includes(p)) return null;

  if (p === PERSONAS.STAFF) {
    // Their own staff role, not a generic one: catalog rows key on the whole
    // owner list, but route guards and audit entries care which operator this
    // is, and substituting a different staff role would misreport that.
    return roles.find((r) => STAFF_ROLES.has(r)) || null;
  }

  if (p === PERSONAS.REVIEWER) {
    return roles.includes('lead_reviewer') ? 'lead_reviewer'
      : roles.includes('reviewer') ? 'reviewer'
      : null;
  }

  // Customer: prefer a customer-track role they actually hold — a `designer`
  // sees designer navigation, which is the point of having those roles — and
  // fall back to the generic `user` for anyone whose only roles are staff.
  const ownCustomerRole = roles.find(
    (r) => !STAFF_ROLES.has(r) && r !== 'reviewer' && r !== 'lead_reviewer'
  );
  return ownCustomerRole || 'user';
};

/**
 * EVERY role this actor holds that belongs to a given persona.
 *
 * `roleForPersona` picks one; this returns the whole set, because the customer
 * persona can legitimately contain several — an account with `user` plus an
 * assigned `designer` sees different navigation under each, and the old raw-
 * role switcher let them choose. Folding roles into personas must not quietly
 * take that away, so the switcher offers this as a sub-choice whenever it has
 * more than one entry.
 */
export const rolesForPersona = (actor, persona) => {
  const p = String(persona || '').toLowerCase();
  if (!personasOf(actor).includes(p)) return [];
  const roles = rolesOf(actor);

  if (p === PERSONAS.STAFF) return roles.filter((r) => STAFF_ROLES.has(r));
  if (p === PERSONAS.REVIEWER) return roles.filter((r) => r === 'reviewer' || r === 'lead_reviewer');

  const own = roles.filter((r) => !STAFF_ROLES.has(r) && r !== 'reviewer' && r !== 'lead_reviewer');
  return own.length ? own : ['user'];
};

export const PREVIEWABLE_PERSONAS = Object.freeze([PERSONAS.GUEST]);

/**
 * Where each persona starts.
 *
 * Switching a hat has to MOVE you. The page you were on belongs to the hat
 * you just took off — an operator on /admin/console who switches to Customer
 * would sit on an admin console their customer hat cannot open, and the route
 * guard would bounce them somewhere arbitrary. Landing on the new persona's
 * own home is the difference between a switch that works and one that leaves
 * you somewhere broken.
 *
 * ⚠️ These are real routes in apps/dashboard/src/routes/routes.jsx. The
 * customer home is `/home`, not `/dashboard` — there is no `/dashboard`
 * route, and pointing at one would send every customer switch to the 404.
 */
export const PERSONA_HOME = Object.freeze({
  [PERSONAS.GUEST]: '/',
  [PERSONAS.CUSTOMER]: '/home',
  [PERSONAS.REVIEWER]: '/reviewer',
  [PERSONAS.STAFF]: '/admin/console'
});

export default {
  PERSONAS, personaForRole, personasOf, personaOptionsOf, defaultPersonaOf,
  personaCapabilities, roleForPersona, rolesForPersona
};
